import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { InlineWidget } from "react-calendly";
import { marked } from "marked";
import DOMPurify from "dompurify";
import BotImage from "./assets/chatbot.png"; // Adjust path if needed
import LogoImage from "./assets/propix.png"; // Adjust path if needed

const DecisionTree = () => {
  // -----------------------
  // 1) Component State
  // -----------------------
  const [initialized, setInitialized] = useState(false);
  const [userSelections, setUserSelections] = useState({});
  const [step, setStep] = useState(0);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [answeredMessages, setAnsweredMessages] = useState([]);
  const [showCalendlyWidget, setShowCalendlyWidget] = useState(true);
  const [showInput, setShowInput] = useState(false);
  const [askQuestion, setAskQuestion] = useState("");

  // For storing conversation data from LangFlow Q&A
  const [langflowConversation, setLangflowConversation] = useState([]);
  const [conversationId, setConversationId] = useState(null); // <-- NEW

  // Whether to hide the welcome banner if the user typed an open-ended question
  const [hideWelcomeMessage, setHideWelcomeMessage] = useState(false);

  // For auto-scrolling
  const messagesEndRef = useRef(null);

  // Your backend API endpoint (Node/Express)
  const API_BASE_URL = "https://chat.propix.ai"; // <-- Adjust if needed

  // -----------------------
  // Inactivity / Session Management States
  // -----------------------
  const lastActivityTime = useRef(Date.now());
  const [showInactivityPrompt, setShowInactivityPrompt] = useState(false);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);

  // This string helps us identify the inactivity prompt message
  const inactivityPromptString =
    "Hey, just checking, are you still there? Do you want to continue?";

  // -----------------------
  // 2) useEffect Hooks
  // -----------------------
  useEffect(() => {
    if (!initialized) {
      startFreshChat();
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showInput]);

  // 1) Periodically check for user inactivity (2 minutes)
  useEffect(() => {
    const checkInactivityInterval = setInterval(() => {
      if (sessionClosed || showInactivityPrompt) return;
      const now = Date.now();
      if (now - lastActivityTime.current > 2 * 60 * 1000) {
        addMessage("bot", inactivityPromptString, ["Yes", "No"]);
        setShowInactivityPrompt(true);
      }
    }, 30 * 1000);
    return () => clearInterval(checkInactivityInterval);
  }, [sessionClosed, showInactivityPrompt]);

  // 2) If inactivity prompt is showing and user doesn't respond => auto close WITHOUT feedback
  useEffect(() => {
    let autoCloseTimer;
    if (showInactivityPrompt && !sessionClosed) {
      autoCloseTimer = setTimeout(() => {
        // auto "No"
        // find the index of the inactivity prompt in messages
        const inactivityMsgIndex = messages.findIndex(
          (m) => m.text === inactivityPromptString
        );
        if (inactivityMsgIndex !== -1) {
          handleOptionSelect("No", inactivityMsgIndex);
        }
      }, 60 * 1000);
    }
    return () => clearTimeout(autoCloseTimer);
    // include messages in dependency so we can always find the inactivity index
  }, [showInactivityPrompt, sessionClosed, messages]);
  

  // If feedback prompt is showing and user doesn't respond => auto close
  useEffect(() => {
    let feedbackTimer;
    if (showFeedbackPrompt && !sessionClosed) {
      feedbackTimer = setTimeout(() => {
        // auto close
        finalizeSession();
      }, 60 * 1000);
    }
    return () => clearTimeout(feedbackTimer);
  }, [showFeedbackPrompt, sessionClosed]);

  // -----------------------
  // 3) Classification
  // -----------------------
  const classifyUserIntent = (question) => {
    const lowerQ = question.toLowerCase();
    if (
      lowerQ.includes("demo") ||
      lowerQ.includes("agent") ||
      lowerQ.includes("connect") ||
      lowerQ.includes("contact an agent") ||
      lowerQ.includes("book a call") ||
      lowerQ.includes("call")
    ) {
      return "Book a Demo";
    }
    if (
      lowerQ.includes("enquire") ||
      lowerQ.includes("question") ||
      lowerQ.includes("chatbot") ||
      lowerQ.includes("about chatbots")
    ) {
      return "Enquire About Chatbots";
    }
    if (
      lowerQ.includes("learn") ||
      lowerQ.includes("roi") ||
      lowerQ.includes("benefits") ||
      lowerQ.includes("real-life examples")
    ) {
      return "Learn About Chatbots";
    }
    if (lowerQ.includes("not sure") || lowerQ.includes("help")) {
      return "Not Sure Yet";
    }
    return "GENERAL";
  };

  // -----------------------
  // 4) Flows
  // -----------------------
  const welcomeMessage = {
    bot: "Hi, i’m Keya! 👋 Welcome to Propix AI! What can I help you with..?",
    options: ["Book a Demo", "Enquire About Chatbots", "Learn About Chatbots", "Not Sure Yet"],
    key: "interest",
  };

  // 1) Book a Demo Flow
  const bookDemoFlow = [
    {
      bot: "Great, can I know who I'm speaking with?",
      input: { type: "text", placeholder: "Enter your name" },
      key: "userName",
    },
    {
      condition: (data) => data.userName,
      bot: "Thanks, {userName}! What’s the best email address in case we get cut off?",
      input: { type: "email", placeholder: "Enter your email" },
      key: "email",
    },
    {
      condition: (data) => data.email,
      bot: "Now let’s get you booked in for a chat with one of our team {userName}.",
      input: { type: "calendly" },
      key: "intro",
    },
    {
      condition: (data) => data.availability,
      bot: "Got it! You’re all set. You’ll be speaking with Tom on {availability}. You’ll receive a confirmation email shortly. Is there anything specific you'd like us to cover during the demo?",
      input: { type: "textarea", placeholder: "Enter demo requirements" },
      key: "demoDetails",
    },
    {
      condition: (data) => data.demoDetails,
      bot: "Great. Can you confirm your phone number, just in case we need to confirm anything?",
      input: { type: "tel", placeholder: "Enter your phone number" },
      key: "phoneNumber",
    },
    {
      condition: (data) => data.phoneNumber,
      bot: "Thank you! We’re excited to show you what Propix AI can do. See you soon!",
      key: "end",
    },
  ];

  // 2) Enquire About Chatbots Flow
  const enquireChatbotsFlow = [
    {
      bot: "Great, can I know who I’m speaking with?",
      input: { type: "text", placeholder: "Enter your name" },
      key: "userName",
    },
    {
      condition: (data) => data.userName,
      bot: "Thanks, {userName}! What’s the best email address in case we get cut off?",
      input: { type: "email", placeholder: "Enter your email" },
      key: "email",
    },
    {
      condition: (data) => data.email,
      bot: "What kind of chatbot are you interested in?",
      options: ["Lead Qualification", "Customer Support", "Both", "Other"],
      key: "chatbotType",
    },
    {
      condition: (data) => data.chatbotType,
      bot: "Got it! How many leads or customers do you handle in a typical month?",
      options: ["Under 100", "100–500", "500+", "Not Sure"],
      key: "leadVolume",
    },
    {
      condition: (data) => data.leadVolume,
      bot: "Are you currently using any chatbot or automation tool?",
      options: ["Yes", "No", "Considering It"],
      key: "existingTool",
    },
    {
      condition: (data) => data.existingTool,
      bot: "Thanks for the details! A team member will reach out shortly with a tailored solution. Is there anything else you’d like to share?",
      input: { type: "textarea", placeholder: "Add your comments" },
      key: "extraDetails",
    },
    {
      condition: () => true,
      bot: "Thank you for sharing! Our team will get in touch soon.",
      key: "end",
    },
  ];

  // 3) Learn About Chatbots Flow
  const learnAboutChatbotsFlow = [
    {
      bot: "Great, can I know who I’m speaking with?",
      input: { type: "text", placeholder: "Enter your name" },
      key: "userName",
    },
    {
      condition: (data) => data.userName,
      bot: "Thanks, {userName}! What’s the best email address in case we get cut off?",
      input: { type: "email", placeholder: "Enter your email" },
      key: "email",
    },
    {
      condition: (data) => data.email,
      bot: "Perfect, thank you! What would you like to learn about chatbots?",
      options: [
        "How they work",
        "Benefits for my business",
        "The ROI of Propix AI",
        "Real-life examples",
      ],
      key: "learningTopic",
    },
    {
      condition: (data) => data.learningTopic === "How they work",
      bot: `
          Here’s a guide: <a href='https://propix.ai/2025/02/13/what-are-propix-ai-agents/' target='_blank'>What are Propix AI agents</a>
  
          <b>1. What Are AI Agents?</b><br/>
          Propix AI Agents are next-gen chatbots designed for the property industry. Unlike standard chatbots, they leverage AI and integrate seamlessly with your business systems to provide smarter, more personalised customer interactions.<br/><br/>
  
          <b>2. Why AI Agents?</b><br/>
          • 24/7 Customer Support – Never miss a lead, even outside office hours.<br/>
          • Lead Qualification – Automatically capture and filter leads based on budget, location, and intent.<br/>
          • Seamless CRM Integration – Connect with your existing systems for efficient follow-ups.<br/>
          • Smarter Conversations – AI-driven responses feel natural and engaging, improving customer experience.<br/>
          • Secure & Customisable – You control which data is used, ensuring security and brand alignment.<br/>

           <br/><br/>Would you like to see a demo or ask a question?<br/><br/>
        `,
      options: ["Yes, book a call", "Ask a question"],
      key: "subAction",
    },
    {
      condition: (data) => data.learningTopic === "Benefits for my business",
      bot: `
          Here’s a guide: <a href='https://propix.ai/2025/02/13/benefits-for-my-business/' target='_blank'>Benefits for my business</a>
          <br/><br/>
          <b>Benefits for Your Business</b><br/><br/>
          1. <b>More Leads, Better Conversions</b><br/>
          Propix AI qualifies leads instantly, collecting key details like name, email, budget, and location—ensuring estate agents only focus on serious buyers and sellers.<br/><br/>
          2. <b>Enhanced Customer Experience</b><br/>
          With real-time chat and intelligent recommendations, customers get the right property suggestions without waiting for a human response.<br/><br/>
          3. <b>Efficient Operations</b><br/>
          &bull; Automated appointment scheduling<br/>
          &bull; Instant answers to FAQs<br/>
          &bull; Consistent brand messaging across all platforms<br/><br/>
          4. <b>Competitive Advantage</b><br/>
          Your competitors may not have AI-driven customer engagement yet. Early adoption means higher customer satisfaction, faster response times, and more closed deals.
          <br/><br/>Do you want to explore a solution for your business?`,
      options: ["Yes, book a demo", "Ask a question"],
      key: "subAction",
    },
    {
      condition: (data) => data.learningTopic === "The ROI of Propix AI",
      bot: `
          Here’s a guide: <a href='https://propix.ai/2025/02/13/roi/' target='_blank'>ROI/Post</a>
          <br/><br/>
          <b>The ROI of Propix AI</b><br/><br/>
          1. <b>The Cost of Missed Leads</b><br/>
          Without AI, estate agents lose potential clients due to slow responses and inefficient lead handling. Every missed enquiry is lost revenue.<br/><br/>
          2. <b>Real Results from Propix AI</b><br/>
          &bull; 30% increase in qualified leads<br/>
          &bull; 20% faster deal closures<br/>
          &bull; Higher customer satisfaction & referrals<br/><br/>
          3. <b>AI That Pays for Itself</b><br/>
          With increased efficiency and more closed deals, the return on investment (ROI) is clear—Propix AI boosts revenue while reducing operational costs.
          <br/><br/>Do you want to speak with a member of our team?`,
      options: ["Yes, speak with a member", "Ask a question"],
      key: "subAction",
    },
    {
      condition: (data) => data.learningTopic === "Real-life examples",
      bot: `
          <b>How Propix AI Transformed Lanka Real Estate</b><br/><br/>
  
          <b>1. Overview</b><br/>
          Lanka Real Estate is a leading property company in Sri Lanka, specialising in buying, selling, and renting. They were getting more enquiries than their team could handle—especially from international clients in different time zones.<br/><br/>
  
          <b>2. Challenges</b><br/>
          • High enquiry volume leading to missed leads<br/>
          • Limited availability outside office hours<br/>
          • Time-consuming manual lead qualification<br/>
          • Inconsistent customer experience across channels<br/><br/>
  
          <b>3. Propix AI Solution</b><br/>
          • 24/7 chatbot assistance for instant responses<br/>
          • Automated lead qualification to identify serious buyers/sellers<br/>
          • Appointment scheduling for property viewings via chat<br/>
          • CRM integration for seamless agent follow-ups<br/><br/>
  
          <b>4. Real-Life Example</b><br/>
          An overseas buyer from the UK searched for beachfront villas on the Lanka Real Estate website. The chatbot welcomed him, asked for his budget, then recommended properties. He booked a virtual viewing on the spot. Once identified as a serious lead, a senior agent was alerted and closed the deal within two weeks.<br/><br/>
  
          <b>5. Results</b><br/>
          • 30% increase in qualified leads<br/>
          • 20% faster deal closures thanks to automated follow-ups<br/>
          • Round-the-clock coverage greatly reduced missed opportunities<br/>
          • Higher customer satisfaction and referral rates<br/><br/>
  
          <b>6. Key Takeaway</b><br/>
          Propix AI enabled Lanka Real Estate to efficiently handle enquiries day and night, qualify leads faster, and close more deals—all while delivering a consistently great customer experience.<br/><br/>
  
          Would you like to schedule a call to learn more?
        `,
      options: ["Yes, book a call", "Ask a question"],
      key: "subAction",
    },
    // Inline booking
    {
      condition: (data) =>
        data.subAction === "Yes, book a call" ||
        data.subAction === "Yes, book a demo" ||
        data.subAction === "Yes, speak with a member",
      bot: "Now let’s get you booked in for a chat with one of our team {userName}.",
      input: { type: "calendly" },
      key: "intro",
    },
    {
      condition: (data) => data.availability,
      bot: "Got it! You’re all set. You’ll be speaking with Tom on {availability}....",
      input: { type: "textarea", placeholder: "Enter demo requirements" },
      key: "demoDetails",
    },
    {
      condition: (data) => data.demoDetails,
      bot: "Great. Can you confirm your phone number...",
      input: { type: "tel", placeholder: "Enter your phone number" },
      key: "phoneNumber",
    },
    {
      condition: (data) => data.phoneNumber,
      bot: "Thank you! We’re excited to show you what Propix AI can do. See you soon!",
      key: "end",
    },
    // If user asked a question
    {
      condition: (data) => data.subAction === "Ask a question",
      bot: "Thanks, {userName}!",
      key: "end",
    },
  ];

  // 4) Not Sure Yet Flow
  const notSureYetFlow = [
    {
      bot: "Perfect, thank you! Can I ask a couple of quick questions to understand your needs?",
      options: ["Yes", "No"],
      key: "followUpQuestions",
    },
    {
      condition: (data) => data.followUpQuestions === "Yes",
      bot: "Are you looking to automate lead qualification or customer support?",
      options: ["Lead Qualification", "Customer Support", "Both", "Other"],
      key: "automationType",
    },
    {
      condition: (data) => data.followUpQuestions === "No",
      bot: "That’s okay! If you ever need more information, just type 'help' or reach out at <a href='mailto:tom@propix.ai'>Contact Us</a>.",
      key: "end",
    },
    {
      condition: (data) => data.automationType,
      bot: "Do you have an existing chatbot solution?",
      options: ["Yes", "No", "Considering"],
      key: "chatbotSolution",
    },
    {
      condition: (data) => data.chatbotSolution,
      bot: "Thanks! Would you be interested in speaking with a member of our team or exploring our resources?",
      options: ["Yes, speak to Propix team", "Explore resources"],
      key: "nextStep",
    },
    {
      condition: (data) => data.nextStep === "Yes, speak to Propix team",
      bot: "Great, can I know who I'm speaking with?",
      input: { type: "text", placeholder: "Enter your name" },
      key: "userName",
    },
    {
      condition: (data) => data.userName,
      bot: "Thanks, {userName}! What’s the best email address in case we get cut off?",
      input: { type: "email", placeholder: "Enter your email" },
      key: "email",
    },
    {
      condition: (data) => data.email,
      bot: "Now let’s get you booked in for a chat with one of our team {userName}.",
      input: { type: "calendly" },
      key: "intro",
    },
    {
      condition: (data) => data.availability,
      bot: "Got it! You’ll be speaking with Tom on {availability}. Is there anything specific you'd like covered?",
      input: { type: "textarea", placeholder: "Enter details" },
      key: "demoDetails",
    },
    {
      condition: (data) => data.demoDetails,
      bot: "Great. Can you confirm your phone number?",
      input: { type: "tel", placeholder: "Enter phone number" },
      key: "phoneNumber",
    },
    {
      condition: (data) => data.phoneNumber,
      bot: "Thank you! We’re excited to show you what Propix AI can do. See you soon!",
      key: "end",
    },
    {
      condition: (data) => data.nextStep === "Explore resources",
      bot: "Here is a helpful blog: <a href='https://propix.ai/article/' target='_blank'>Propix Blog</a>",
      key: "end",
    },
  ];

  const getFlowByInterest = (interest) => {
    switch (interest) {
      case "Book a Demo":
        return bookDemoFlow;
      case "Enquire About Chatbots":
        return enquireChatbotsFlow;
      case "Learn About Chatbots":
        return learnAboutChatbotsFlow;
      case "Not Sure Yet":
        return notSureYetFlow;
      default:
        return [];
    }
  };

  // -----------------------
  // 5) Chat Initialization
  // -----------------------
  const startFreshChat = () => {
    setMessages([
      {
        sender: "bot",
        text: welcomeMessage.bot,
        options: welcomeMessage.options,
      },
    ]);
  };

  // -----------------------
  // 6) Messaging Logic
  // -----------------------
  const addMessage = (sender, text, options = null) => {
    const timestamp = new Date();
    setMessages((prev) => [...prev, { sender, text, options, timestamp }]);
  };

  const resetChat = () => {
    setUserSelections({});
    setStep(0);
    setMessages([]);
    setAnsweredMessages([]);
    setShowCalendlyWidget(true);
    setShowInput(false);
    setHideWelcomeMessage(false);

    // Also reset session management
    setSessionClosed(false);
    setShowInactivityPrompt(false);
    setShowFeedbackPrompt(false);
    lastActivityTime.current = Date.now();
    setConversationId(null); // <-- NEW: reset ID

    startFreshChat();
  };

  const userHasProvidedInput = () =>
    Object.keys(userSelections).length > 0 ||
    messages.some((msg) => msg.sender === "user");

  const closeSession = () => {
    setSessionClosed(true);
    setShowInactivityPrompt(false);
    setShowFeedbackPrompt(false);
    addMessage("bot", "Session closed. Thanks for visiting Propix AI! Refresh to start again.");
  };

  // -----------------------
  // 7) Submit/Update Conversation (NEW)
  // -----------------------
  const saveConversation = async () => {
    try {
      const payload = {
        userSelections,
        conversation: messages,
        langflowConversation,
      };

      if (!userHasProvidedInput()) {
        // If user hasn't provided anything at all, skip saving
        return;
      }

      if (!conversationId) {
        // Post to create
        const res = await axios.post(`${API_BASE_URL}/chat`, payload);
        console.log("Conversation created =>", res.data);
        if (res.data.id) {
          setConversationId(res.data.id);
        }
      } else {
        // Put to update existing doc
        await axios.put(`${API_BASE_URL}/chat/${conversationId}`, {
          userSelections,
          langflowConversation,
        });
        console.log("Conversation updated => ", conversationId);
      }
    } catch (error) {
      console.error("Error saving conversation:", error.message);
    }
  };

  const finalizeSession = async () => {
    await saveConversation(); // ensure rating, etc. are saved
    closeSession();
  };

  // -----------------------
  // 8) Option/Text Handlers
  // -----------------------
  const handleOptionSelect = (optionValue, messageIndex) => {
    if (sessionClosed) return;
    lastActivityTime.current = Date.now();

    // If inactivity prompt is showing:
    if (showInactivityPrompt) {
      addMessage("user", optionValue);
      setAnsweredMessages((prev) => [...prev, messageIndex]);

      if (optionValue === "Yes") {
        setShowInactivityPrompt(false);
        // Stay on the same step
        processNextStep(step, userSelections);
      }else if (optionValue === "No") {
        // "No" => ask for feedback
        setShowInactivityPrompt(false);
        setShowFeedbackPrompt(true);
        addMessage("bot", "Please rate your experience with Propix AI:", [
          "1 😒",
          "2 😐",
          "3 😕",
          "4 🙂",
          "5 😃",
        ]);
      } else if (optionValue === "AutoCloseInactivityNoResponse") {
        // auto-close session without feedback
        setShowInactivityPrompt(false);
        closeSession();
      }
      return;
    }

    // If user is selecting rating from feedback prompt
    if (showFeedbackPrompt) {
      addMessage("user", optionValue);
      setAnsweredMessages((prev) => [...prev, messageIndex]);

      // store rating => then finalize
      setUserSelections((prev) => ({ ...prev, userRating: optionValue }));
      addMessage("bot", `Thanks for your rating "${optionValue}".`);

      // finalize session => saves conversation
      setShowFeedbackPrompt(false);
      finalizeSession();
      return;
    }

    // Otherwise normal flow
    setAnsweredMessages((prev) => [...prev, messageIndex]);
    addMessage("user", optionValue);

    // If this is the first-time interest
    if (!userSelections.interest) {
      const newSelections = { ...userSelections, interest: optionValue };
      setUserSelections(newSelections);
      processNextStep(0, newSelections);
      return;
    }

    // continue existing flow
    const flow = getFlowByInterest(userSelections.interest);
    if (!flow.length || step >= flow.length) return;
    const currentStep = flow[step];
    if (!currentStep) return;

    const newSelections = { ...userSelections, [currentStep.key]: optionValue };
    setUserSelections(newSelections);
    processNextStep(step + 1, newSelections);
  };

  const handleTextSubmit = (inputValue, messageIndex) => {
    if (sessionClosed) return;
    lastActivityTime.current = Date.now();

    const flow = getFlowByInterest(userSelections.interest);
    if (!flow.length || step >= flow.length) return;
    const currentStep = flow[step];
    if (!currentStep) return;

    if (
      currentStep.input?.type === "email" &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue)
    ) {
      addMessage("bot", "Oops! That email doesn’t look right. Please try again.");
      return;
    }

    setAnsweredMessages((prev) => [...prev, messageIndex]);
    addMessage("user", inputValue);

    const newSelections = { ...userSelections, [currentStep.key]: inputValue };
    setUserSelections(newSelections);
    processNextStep(step + 1, newSelections);
  };

  const processNextStep = async (stepIndex, selections) => {
    const flow = getFlowByInterest(selections.interest);
    if (stepIndex >= flow.length) return;
    const nextStepObj = flow[stepIndex];

    if (nextStepObj.condition && !nextStepObj.condition(selections)) {
      processNextStep(stepIndex + 1, selections);
      return;
    }

    if (nextStepObj.key === "end") {
      showBotMessage(nextStepObj, selections);
      setStep(flow.length);

      // user finished => save conversation if they have input
      await saveConversation();
      return;
    }

    setStep(stepIndex);
    showBotMessage(nextStepObj, selections);
  };

  const showBotMessage = (stepObj, selections) => {
    setShowInput(false);
    setLoading(true);

    let botMessage;
    if (typeof stepObj.bot === "function") {
      botMessage = stepObj.bot(selections);
    } else {
      botMessage = stepObj.bot.replace(
        /{(\w+)}/g,
        (_, key) => selections[key] || `{${key}}`
      );
    }

    const stepOptions = stepObj.options || null;
    addMessage("bot", botMessage, stepOptions);
    setLoading(false);

    // If we have an input (besides Calendly) we show input after a small delay
    if (stepObj.input && stepObj.input.type !== "calendly") {
      setTimeout(() => setShowInput(true), 1000);
    } else {
      setShowInput(true);
    }
  };

  // -----------------------
  // 9) Submitting to backend in free chat (LangFlow)
  // -----------------------
  const handleCalendlyScheduled = (calendlyEvent) => {
    if (sessionClosed) return;
    lastActivityTime.current = Date.now();

    let event, invitee;
    if (calendlyEvent.data && calendlyEvent.data.event) {
      ({ event, invitee } = calendlyEvent.data);
    } else {
      ({ event, invitee } = calendlyEvent);
    }
    if (!event || !invitee) return;

    const { start_time } = event;
    if (!start_time || !invitee.email || !invitee.name) return;

    setShowCalendlyWidget(false);
    addMessage(
      "bot",
      `✅ Your meeting is scheduled for ${new Date(start_time).toLocaleString()}.`
    );

    const updatedSelections = {
      ...userSelections,
      availability: new Date(start_time).toLocaleString(),
    };
    setUserSelections(updatedSelections);

    setTimeout(() => {
      processNextStep(step + 1, updatedSelections);
    }, 200);
  };

  const handleFreeChatSubmit = async () => {
    if (sessionClosed) return;
    if (!askQuestion.trim()) return;

    lastActivityTime.current = Date.now();
    setHideWelcomeMessage(true);

    // remove welcome msg if it was first
    setMessages((prev) => {
      if (
        prev.length > 0 &&
        prev[0].sender === "bot" &&
        prev[0].text.includes("Welcome to Propix AI")
      ) {
        return prev.slice(1);
      }
      return prev;
    });

    addMessage("user", askQuestion);
    const question = askQuestion;
    setAskQuestion("");
    setLoading(true);

    const intent = classifyUserIntent(question);
    if (intent !== "GENERAL") {
      setUserSelections({});
      setAnsweredMessages([]);
      setStep(0);

      const newSelections = { interest: intent };
      setUserSelections(newSelections);
      setTimeout(() => processNextStep(0, newSelections), 800);
      setLoading(false);
      return;
    }

    // otherwise => call /langflow
    try {
      const response = await fetch(`${API_BASE_URL}/langflow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inputValue: question }),
      });
      if (!response.ok)
        throw new Error(`LangFlow error. Status: ${response.status}`);

      const data = await response.json();
      // Typically the content is in data.result or outputs
      const rawMarkdown =
        data?.result ||
        data?.outputs?.[0]?.outputs?.[0]?.results?.message?.text ||
        "Sorry, I couldn't process that.";
      const rawHTML = marked(rawMarkdown);
      const sanitizedHTML = DOMPurify.sanitize(rawHTML);

      // Add to local state for Q/A
      setLangflowConversation((prev) => {
        const updated = [
          ...prev,
          { sender: "user", text: question },
          { sender: "bot", text: sanitizedHTML },
        ];
        // Also update conversation on server
        saveConversation();
        return updated;
      });

      addMessage("bot", sanitizedHTML);
    } catch (error) {
      console.error("Error with /langflow:", error);
      addMessage("bot", "There was an error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------
  // 10) Rendering
  // -----------------------
  if (sessionClosed) {
    return (
      <div className="chat-widget">
        <div className="chat-header">
          <div className="header-content">
            <img src={LogoImage} alt="Logo" className="header-logo" />
            <h4 className="header-text">AI Live Chat</h4>
          </div>
          <button className="reset-button" onClick={resetChat}>
            <svg
              className="reset-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="#FFFFFF"
            >
              <path d="M19.91,15.51H15.38a1,1,0,0,0,0,2h2.4A8,8,0,0,1,4,12a1,1,0,0,0-2,0,10,10,0,0,0,16.88,7.23V21a1,1,0,0,0,2,0V16.5A1,1,0,0,0,19.91,15.51ZM15,12a3,3,0,1,0-3,3A3,3,0,0,0,15,12Zm-4,0a1,1,0,1,1,1,1A1,1,0,0,1,11,12ZM12,2A10,10,0,0,0,5.12,4.77V3a1,1,0,0,0-2,0V7.5a1,1,0,0,0,1,1h4.5a1,1,0,0,0,0-2H6.22A8,8,0,0,1,20,12a1,1,0,0,0,2,0A10,10,0,0,0,12,2Z" />
            </svg>
          </button>
        </div>

        <div className="chat-messages">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`chat-message ${msg.sender === "user" ? "right" : "left"}`}
            >
              {msg.sender === "bot" && (
                <img src={BotImage} alt="Chat Bot" className="bot-avatar" />
              )}
              <div className="message-text">
                {msg.sender === "bot" ? (
                  <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                ) : (
                  <span>{msg.text}</span>
                )}
                {msg.timestamp && (
                  <div className="message-timestamp">
                    {new Date(msg.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                )}
                {msg.options && (
                  <div className="decision-options">
                    {msg.options.map((option, idx) => (
                      <button key={idx} className="decision-button" disabled>
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="chat-footer">
          Powered by{" "}
          <a href="https://propix.ai" target="_blank" rel="noopener noreferrer">
            Propix.ai ⚡
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-widget">
      {/* Header */}
      <div className="chat-header">
        <div className="header-content">
          <img src={LogoImage} alt="Logo" className="header-logo" />
          <h4 className="header-text">AI Live Chat</h4>
        </div>
        <button className="reset-button" onClick={resetChat}>
          <svg
            className="reset-icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="#FFFFFF"
          >
            <path d="M19.91,15.51H15.38a1,1,0,0,0,0,2h2.4A8,8,0,0,1,4,12a1,1,0,0,0-2,0,10,10,0,0,0,16.88,7.23V21a1,1,0,0,0,2,0V16.5A1,1,0,0,0,19.91,15.51ZM15,12a3,3,0,1,0-3,3A3,3,0,0,0,15,12Zm-4,0a1,1,0,1,1,1,1A1,1,0,0,1,11,12ZM12,2A10,10,0,0,0,5.12,4.77V3a1,1,0,0,0-2,0V7.5a1,1,0,0,0,1,1h4.5a1,1,0,0,0,0-2H6.22A8,8,0,0,1,20,12a1,1,0,0,0,2,0A10,10,0,0,0,12,2Z" />
          </svg>
        </button>
      </div>

      {/* Chat messages container */}
      <div className="chat-messages">
        {messages.map((message, index) => {
          const isFirstBotMessage = index === 0 && message.sender === "bot";
          const firstMessageAnswered = answeredMessages.includes(index);

          // Show welcome container if first message not answered,
          // user hasn't selected interest, and we haven't hidden it
          if (
            isFirstBotMessage &&
            !firstMessageAnswered &&
            !userSelections.interest &&
            !hideWelcomeMessage
          ) {
            return (
              <div key={index} className="welcome-message-container">
                <div className="welcome-text">
                  <h5>
                    Hey, I’m Keya, the Propix AI Assistant. Need any help?
                  </h5>
                  {message.options && (
                    <div className="decision-options first-message-options">
                      {message.options.map((option, idx) => (
                        <button
                          key={idx}
                          className="decision-button first-message-button"
                          onClick={() => handleOptionSelect(option, index)}
                          disabled={answeredMessages.includes(index)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          }

          return (
            <div
              key={index}
              className={`chat-message ${
                message.sender === "user" ? "right" : "left"
              } ${answeredMessages.includes(index) ? "answered" : ""}`}
            >
              {message.sender === "bot" && (
                <img src={BotImage} alt="Chat Bot" className="bot-avatar" />
              )}
              <div className="message-text">
                {message.sender === "bot" ? (
                  <div dangerouslySetInnerHTML={{ __html: message.text }} />
                ) : (
                  <span>{message.text}</span>
                )}
                {message.timestamp && (
                  <div className="message-timestamp">
                    {new Date(message.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                )}
                {message.options && (
                  <div className="decision-options">
                    {message.options.map((option, idx) => (
                      <button
                        key={idx}
                        className="decision-button"
                        onClick={() => handleOptionSelect(option, index)}
                        disabled={
                          answeredMessages.includes(index) ||
                          (showInactivityPrompt &&
                            message.text !== inactivityPromptString)
                        }
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {loading && (
          <div className="chat-message left">
            <div className="loading-dots">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Flow Input Area / Calendly */}
      {userSelections.interest &&
        step < getFlowByInterest(userSelections.interest).length &&
        (() => {
          const flow = getFlowByInterest(userSelections.interest);
          const stepObj = flow[step];
          if (!stepObj?.input) return null;

          if (stepObj.input.type === "calendly") {
            return showCalendlyWidget ? (
              <div className="calendly-widget-container">
                <InlineWidget
                  url="https://calendly.com/tom-propix/30min"
                  prefill={{
                    email: userSelections.email,
                    name: userSelections.userName,
                  }}
                  onEventScheduled={handleCalendlyScheduled}
                  styles={{
                    height: "400px",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                  }}
                />
                <div className="back-button">
                  <button
                    onClick={() => {
                      setShowCalendlyWidget(false);
                      addMessage(
                        "bot",
                        "What’s your availability? Please pick a time slot. 😊"
                      );
                      const updatedSelections = {
                        ...userSelections,
                        availability: "Scheduled via Calendly",
                      };
                      setUserSelections(updatedSelections);
                      setTimeout(() => {
                        processNextStep(step + 1, updatedSelections);
                      }, 200);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : null;
          }

          if (!showInput) return null;

          const latestMessageIndex = messages.length - 1;
          const isDisabled = answeredMessages.includes(latestMessageIndex) || showInactivityPrompt || loading;

          if (stepObj.input.type === "textarea") {
            return (
              <div className="decision-input-container">
                <div className="decision-input">
                  <textarea
                    rows={3}
                    placeholder={stepObj.input.placeholder}
                    disabled={isDisabled}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleTextSubmit(e.target.value, latestMessageIndex);
                        e.target.value = "";
                      }
                    }}
                  />
                  <button
                    disabled={isDisabled}
                    onClick={(e) => {
                      const inputEl = e.target.previousSibling;
                      handleTextSubmit(inputEl.value, latestMessageIndex);
                      inputEl.value = "";
                    }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            );
          } else {
            return (
              <div className="decision-input-container">
                <div className="decision-input">
                  <input
                    type={stepObj.input.type}
                    placeholder={stepObj.input.placeholder}
                    disabled={isDisabled}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleTextSubmit(e.target.value, latestMessageIndex);
                        e.target.value = "";
                      }
                    }}
                  />
                  <button
                    disabled={isDisabled}
                    onClick={(e) => {
                      const inputEl = e.target.previousSibling;
                      handleTextSubmit(inputEl.value, latestMessageIndex);
                      inputEl.value = "";
                    }}
                  >
                    ➤
                  </button>
                </div>
              </div>
            );
          }
        })()}

      {/* Free Chat / Ask Me Anything Input */}
      {(!userSelections.interest ||
        step >= getFlowByInterest(userSelections.interest).length) && (
        <div className="ask-question-container">
          <div className="decision-input">
            <input
              type="text"
              placeholder="Ask me anything ..."
              value={askQuestion}
              onChange={(e) => setAskQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleFreeChatSubmit();
                }
              }}
              disabled={loading || showInactivityPrompt}
            />
            <button
              onClick={handleFreeChatSubmit}
              disabled={!askQuestion.trim() || loading || showInactivityPrompt}
            >
              ➤
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="chat-footer">
        Powered by{" "}
        <a
          href="https://propix.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="footer-link"
        >
          - Propix.ai ⚡
        </a>
      </div>
    </div>
  );
};

export default DecisionTree;
